import { Component, OnInit } from '@angular/core';
import { CustomerModel } from '@core/models/customer.model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PaymentAgreementService } from '@modules/payment-agreement/services/payment-agreement.service';
import { CustomerService } from '@modules/customers/customer.service';
import { ToastService } from '@services/ui';
import { CustomerCaseWorkflowI } from '@shared/components/send-propose-payment-agreement/domain/customerCaseWorkflowInstance';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CurrencyHelper } from '@shared/helpers/decimals.helper';

@Component({
  selector: 'app-send-propose-payment-agreement-modal',
  templateUrl: './send-propose-payment-agreement-modal.component.html',
  styleUrls: ['./send-propose-payment-agreement-modal.component.scss'],
})
export class SendProposePaymentAgreementModalComponent implements OnInit {
  customer: CustomerModel = {} as CustomerModel;
  customerCase: CustomerCaseWorkflowI = {} as CustomerCaseWorkflowI;
  sms = '';
  email = '';
  nMonths = 1;
  installments: any[] = [];

  selectedChannel = '';
  expirationDate: Date = new Date(Date.now() + 72 * 60 * 60 * 1000);
  minDate: Date = new Date();
  customMode = false;
  customMonths = 13;
  hasSms = false;
  hasEmail = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private customerService: CustomerService,
    private paymentAgreementService: PaymentAgreementService,
    private toast: ToastService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.initVariables();
    this.loadInstallments(1);
  }

  initVariables(): void {
    this.customerCase = this.config.data;
    this.customerService.getCustomer(this.customerCase.customerId).then((res: any) => {
      this.customer = res;
      this.hasSms = Array.isArray(this.customer.phones) && this.customer.phones.length > 0;
      this.hasEmail =
        Array.isArray(this.customer.emailAddresses) && this.customer.emailAddresses.length > 0;
      if (this.hasSms) {
        this.selectedChannel = 'sms';
        this.sms = this.customer.phones?.[0] ?? '';
      } else if (this.hasEmail) {
        this.selectedChannel = 'email';
        this.email = this.customer.emailAddresses?.[0] ?? '';
      }
    });
  }

  private loadInstallments(months: number): void {
    this.paymentAgreementService
      .calculatePaymentAgreementInstalments(
        this.customerCase.customerCaseId || this.customerCase.id,
        months,
      )
      .subscribe((response) => {
        if (response.errors) {
          this.toast.showError('Error', response.errors[0].message);
          return;
        }
        this.installments = response;
      });
  }

  selectChannel(channel: string): void {
    this.selectedChannel = channel;
    if (channel === 'sms') {
      this.sms = this.customer.phones?.[0] || '';
      this.email = '';
    } else {
      this.email = this.customer.emailAddresses?.[0] || '';
      this.sms = '';
    }
  }

  setMonths(n: number): void {
    this.nMonths = n;
    this.customMode = false;
    this.loadInstallments(n);
  }

  enableCustomMode(): void {
    this.customMode = true;
    this.customMonths = this.nMonths > 12 ? this.nMonths : 13;
    this.nMonths = this.customMonths;
    this.loadInstallments(this.nMonths);
  }

  applyCustomMonths(): void {
    const n = Math.max(1, Math.min(24, this.customMonths));
    this.customMonths = n;
    this.nMonths = n;
    this.loadInstallments(n);
  }

  cancel(): void {
    this.ref.close();
  }

  checkDisabled(): boolean {
    return !this.selectedChannel || !this.nMonths || !this.customerCase?.id;
  }

  send(): void {
    this.ref.close({
      channel: this.selectedChannel,
      customerCase: this.customerCase,
      nMonths: this.nMonths,
      expirationAt: this.expirationDate,
    });
  }

  getEstimatedDate(index: number): Date {
    const date = new Date(this.expirationDate);
    date.setMonth(date.getMonth() + index);
    return date;
  }

  get expirationDays(): number {
    if (!this.expirationDate) return 0;
    const sel = new Date(
      this.expirationDate.getFullYear(),
      this.expirationDate.getMonth(),
      this.expirationDate.getDate(),
    );
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((sel.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  get pendingAmountNumeric(): string {
    const amount = this.customerCase?.pendingAmount?.amount;
    if (!amount) return '0,00';
    return new Intl.NumberFormat('es', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }).format(CurrencyHelper.toDecimal(amount));
  }

  get pendingCurrencyCode(): string {
    return (
      this.customerCase?.pendingAmount?.currency ||
      this.customerCase?.totalAmountCurrency ||
      ''
    )
      .slice(0, 3)
      .toUpperCase();
  }

  get summaryHtml(): SafeHtml {
    const name = this.customer?.longName || this.customer?.name || '';
    const totalAmount = this.formatAmount(
      this.customerCase?.pendingAmount?.amount,
      this.customerCase?.pendingAmount?.currency || this.customerCase?.totalAmountCurrency,
    );
    const cuotaKey =
      this.nMonths === 1
        ? 'DATA.MODULES.SHARED.COMPONENTS.SEND_PAYMENT_AGREEMENT.CUOTA'
        : 'DATA.MODULES.SHARED.COMPONENTS.SEND_PAYMENT_AGREEMENT.CUOTAS';
    const instalmentText = `${this.nMonths} ${this.translate.instant(cuotaKey)}`;
    const instalmentDetail =
      this.nMonths > 1 && this.installments.length
        ? ` de ${this.formatAmount(this.installments[0]?.amount?.amount, this.installments[0]?.amount?.currency)}`
        : '';
    const channel = this.selectedChannel === 'sms' ? 'SMS' : 'Email';
    const d = this.expirationDate;
    const dateStr = d
      ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
      : '';
    const html = this.translate.instant(
      'DATA.MODULES.SHARED.COMPONENTS.SEND_PAYMENT_AGREEMENT.SUMMARY',
      { customerName: name, totalAmount, instalmentText, instalmentDetail, channel, expirationDate: dateStr },
    );
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private formatAmount(amount: number, currency: string): string {
    const formatted = new Intl.NumberFormat('es', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }).format(CurrencyHelper.toDecimal(amount || 0));
    return `${formatted} ${(currency || '').slice(0, 3).toUpperCase()}`;
  }
}
